import {
  type FilterImplOptions,
  Liquid,
} from "@hightouch/lib/templating/liquid";
// Browser-safe copy of the common Liquid filters — parity-tested against the SDK
// original, which can't be imported here (pulls in apollo-server-express + Node).
// The by-name map (not the array) is what we register: this factory also builds
// the frontend preview engine, where minification mangles `Function.name`, so a
// `fn.name` loop would bind every filter to a garbage name.
import { commonFiltersByName } from "@hightouch/lib/templating/functions";
import { registerDynamicSlotTag } from "./dynamic-content/liquid-tag";
import { registerCepLiquidFilters } from "./liquid-filters";
import { registerContentBlockTag } from "./content-blocks/liquid-tag";
import { registerLocalizationTags } from "./localization/liquid-tag";

export type CreateCepLiquidEngineOptions = {
  /**
   * Named filters layered on top of the common set — e.g. the backend-only
   * `hmac_sha256` (node:crypto). Injected rather than imported so this module
   * stays browser-safe.
   */
  extraFilters?: Record<string, FilterImplOptions>;
};

/**
 * Build the CEP Liquid engine. Node-free (no node:crypto, SDK functions, or the
 * content-blocks DB barrel) so the dispatcher, backend preview API, and browser
 * preview all construct it identically.
 *
 * - strictFilters/strictVariables false: missing vars render empty, so no raw
 *   `{{placeholders}}` reach delivered emails.
 * - cache true: parse each template AST once, reuse across rows.
 *
 * Filter precedence: commonFilters, then cepLiquidFilters, then extraFilters
 * (so a stub can override), then the content-block, dynamic-slot and localization
 * tags.
 *
 * The localization tag must register in every engine this factory builds: an
 * unregistered tag is a LiquidJS *parse* error, and a stamped body can reach any of
 * them. An engine never given a translations map renders every line in its default
 * language rather than failing.
 */
export function createCepLiquidEngine(
  options: CreateCepLiquidEngineOptions = {},
): Liquid {
  const engine = new Liquid({
    strictFilters: false,
    strictVariables: false,
    cache: true,
  });
  for (const [name, fn] of Object.entries(commonFiltersByName)) {
    engine.registerFilter(name, fn as FilterImplOptions);
  }
  registerCepLiquidFilters(engine);
  for (const [name, fn] of Object.entries(options.extraFilters ?? {})) {
    engine.registerFilter(name, fn);
  }
  registerContentBlockTag(engine);
  registerDynamicSlotTag(engine);
  registerLocalizationTags(engine);
  return engine;
}
